import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { showSuccessToast, handleAPIError } from '@/utils/toasts'
import { getUserFacingError } from '@/utils/errorMessages'
import { APIRoutes } from '@/services/api.routes'
import { faTimesCircle, faSpinner, faCheckCircle, faExclamationTriangle } from '@/fontawesome'
import PasswordInput from '@/components/forms/PasswordInput'
import type { UserID } from '@/models'
import { useDarkMode } from '@/hooks/useDarkMode'
import DarkModeToggle from '@/components/common/DarkModeToggle'

export default function ResetPassword() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const { isDarkMode } = useDarkMode()

  // Parse URL parameters
  const uid = searchParams.get('uid') as UserID | null
  const token = searchParams.get('token')
  const expiryTimestamp = searchParams.get('expiry')

  // Check if token is expired
  const isTokenExpired = expiryTimestamp !== null && parseInt(expiryTimestamp) * 1000 < Date.now()

  // Validate URL parameters on mount
  useEffect(() => {
    if (uid === null || token === null) {
      setError('Ungültiger Reset-Link. Bitte fordere einen neuen Link an.')
    }
  }, [uid, token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Validate passwords
    if (password.length < 8) {
      setError('Das Passwort muss mindestens 8 Zeichen lang sein')
      return
    }

    if (password !== passwordConfirm) {
      setError('Die Passwörter stimmen nicht überein')
      return
    }

    if (uid === null || token === null) {
      setError('Ungültiger Reset-Link')
      return
    }

    setLoading(true)

    try {
      await APIRoutes.resetPassword(uid, password.trim(), token)
      showSuccessToast('Passwort erfolgreich zurückgesetzt!')

      // Redirect to login after 1 second
      setTimeout(() => {
        navigate('/login')
      }, 1000)
    } catch (err) {
      setError(getUserFacingError(err, 'Dein Link zum Zurücksetzen des Passworts ist ungültig oder abgelaufen. Bitte fordere über „Passwort vergessen“ einen neuen Link an.'))
      handleAPIError(err, 'Fehler beim Zurücksetzen des Passworts')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 px-4 relative">
      <DarkModeToggle className="absolute top-4 right-4" />
      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8 space-y-8">
          <div className="text-center">
            <div className="mb-6">
              <img
                src={isDarkMode ? "/logo_light.png" : "/logo.png"}
                alt="Börsencampus Logo"
                className="h-16 object-contain mx-auto my-6"
              />
              <div className="w-20 h-1 bg-blue-600 mx-auto rounded-full"></div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Passwort zurücksetzen
            </h2>
            <p className="text-gray-600 dark:text-gray-300">
              Gib dein neues Passwort ein
            </p>
          </div>

          {/* Token expired warning */}
          {isTokenExpired && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <p className="text-yellow-700 dark:text-yellow-400 text-sm font-medium">
                    Dieser Link ist abgelaufen. Bitte fordere einen neuen Link an.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Invalid link error */}
          {(uid === null || token === null) && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FontAwesomeIcon icon={faTimesCircle} className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-red-700 dark:text-red-400 text-sm font-medium">
                    Ungültiger Reset-Link. Bitte fordere einen neuen Link an.
                  </p>
                </div>
              </div>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <PasswordInput
                  label={'Neues Password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mindestens 8 Zeichen"
                  required
                  disabled={loading || uid === null || token === null || isTokenExpired}
                />
              </div>

              <div>
                <PasswordInput
                  label={'Passwort bestätigen'}
                  id="passwordConfirm"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  placeholder="Passwort wiederholen"
                  required
                  disabled={loading || uid === null || token === null || isTokenExpired}
                />
              </div>
            </div>

            {error !== '' && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <FontAwesomeIcon icon={faTimesCircle} className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-red-700 dark:text-red-400 text-sm font-medium">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || uid === null || token === null || isTokenExpired}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                  Passwort wird zurückgesetzt...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheckCircle} className="-ml-1 mr-3 h-5 w-5 text-white" />
                  Passwort zurücksetzen
                </>
              )}
            </button>
          </form>

          <div className="text-center space-y-3">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                  oder
                </span>
              </div>
            </div>

            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Zurück zur{' '}
                <Link
                  to="/login"
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 font-medium"
                >
                  Anmeldung
                </Link>
              </p>
            </div>
          </div>

          <div className="text-center pt-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Powered by SvenBolz Trading Academy
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
